/*
 * cp-gift-threshold.js — CafePod free Storage Tin over £50.
 *
 * Shopify cannot do this with a discount. Buy-X-get-Y makes a product free but
 * will not put it in the basket, and discount functions cannot add lines either.
 * So the mechanic is the one this store already uses for gifts: a £0.00
 * "Value Add" product (ACCPRGTIN1-VA) that the theme adds and removes.
 *
 * Because the tin is already £0.00 there is no discount object to maintain —
 * the line simply costs nothing.
 *
 * Runs site-wide from snippets/scripts.liquid so the tin is in the basket the
 * moment the threshold is crossed, wherever that happens.
 *
 * The qualifying subtotal MUST match snippets/cp-tier-bar.liquid, or the tier
 * bar will promise a tin this script does not add.
 */
(() => {
  const root = document.querySelector('[data-cp-gift]');
  if (!root || window.__cpGiftInit) return;
  window.__cpGiftInit = true;

  const config = {
    threshold: parseInt(root.dataset.threshold, 10) || 0,
    variantId: parseInt(root.dataset.variantId, 10) || 0,
    basis: root.dataset.basis === 'discounted' ? 'discounted' : 'rrp',
    inWindow: root.dataset.inWindow === 'true',
    exclude: (root.dataset.excludeTypes || '')
      .split(',')
      .map((type) => type.trim().toLowerCase())
      .filter(Boolean),
  };

  if (!config.threshold || !config.variantId) return;

  /*
   * Circuit breaker. If a cart write fails once — tin out of stock, a race with
   * another script, anything — we stop for the rest of the session instead of
   * retrying on every cart event. A gift that silently does not appear is much
   * better than a request loop against the cart.
   */
  const FAIL_KEY = 'cp_gift_off';
  const isOff = () => {
    try { return sessionStorage.getItem(FAIL_KEY) === '1'; } catch (error) { return false; }
  };
  const turnOff = () => {
    try { sessionStorage.setItem(FAIL_KEY, '1'); } catch (error) { /* private mode */ }
  };

  const routes = (window.Theme && window.Theme.routes) || {};
  const addUrl = routes.cart_add_url || '/cart/add.js';
  const baseChange = routes.cart_change_url || '/cart/change';
  const changeUrl = baseChange.endsWith('.js') ? baseChange : baseChange + '.js';
  const readUrl = (routes.cart_url || '/cart') + '.js';

  const post = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(url + ' responded ' + response.status);
    return response.json();
  };

  /** Same rules as the tier bar: no subscriptions, no excluded types, and the
   *  tin never counts towards the threshold that earns it. */
  const qualifyingSubtotal = (cart) => {
    let subtotal = 0;
    for (const item of cart.items || []) {
      if (item.variant_id === config.variantId) continue;
      if (item.selling_plan_allocation) continue;

      if (config.exclude.length) {
        const type = String(item.product_type || '').toLowerCase();
        if (config.exclude.includes(type)) continue;
      }

      const rrp = item.original_line_price;
      const net = item.final_line_price;
      const fallback = item.line_price || 0;

      subtotal += config.basis === 'discounted'
        ? (net == null ? fallback : net)
        : (rrp == null ? fallback : rrp);
    }
    return subtotal;
  };

  const findGift = (cart) =>
    (cart.items || []).find((item) => item.variant_id === config.variantId) || null;

  let busy = false;
  let queued = false;

  async function sync() {
    if (isOff()) return;
    if (busy) { queued = true; return; }

    busy = true;
    try {
      const response = await fetch(readUrl, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) return;
      const cart = await response.json();

      const gift = findGift(cart);
      const qualifies = config.inWindow && qualifyingSubtotal(cart) >= config.threshold;
      let changed = false;

      if (qualifies && !gift) {
        await post(addUrl, { items: [{ id: config.variantId, quantity: 1 }] });
        changed = true;
      } else if (qualifies && gift.quantity !== 1) {
        // Someone bumped the free tin. One per order.
        await post(changeUrl, { id: gift.key, quantity: 1 });
        changed = true;
      } else if (!qualifies && gift) {
        // Basket dropped back under the threshold, or the window closed.
        await post(changeUrl, { id: gift.key, quantity: 0 });
        changed = true;
      }

      if (changed) {
        // Tell the theme (and the tier bar) to repaint. Tagged so our own
        // listener ignores the echo.
        document.dispatchEvent(
          new CustomEvent('cart:update', { bubbles: true, detail: { source: 'cp-gift-threshold' } })
        );
      }
    } catch (error) {
      turnOff();
    } finally {
      busy = false;
      if (queued) { queued = false; sync(); }
    }
  }

  document.addEventListener('cart:update', (event) => {
    if (event.detail && event.detail.source === 'cp-gift-threshold') return;
    sync();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', sync);
  else sync();
})();
